
import React from 'react';
import { Search, Filter, Plus, Sparkles, PieChart, Layers } from 'lucide-react';
import { Button } from '../Button';
import { ReportLanguage } from '../../types';

interface Props {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  filterMode: 'active' | 'history';
  itemCount: number;
  onAddItem: () => void;
  // Mobil: startet den Erfassungs-Wizard; ohne Callback kein mobiler Plus-Button
  onMobileAdd?: () => void;
  onAiAssistGlobal: () => void;
  language?: ReportLanguage;
  /**
   * Mängelkatalog (jede Berichtssprache, englisch bei 'en'): Knopf
   * „Standardmangel“ neben „Textvorschlag“ öffnet den Auswahl-Dialog für einen
   * NEUEN Eintrag. Ohne Callback kein Knopf.
   */
  onKatalog?: () => void;
  /** Themen (Katalog-Kategorien) der Liste mit Anzahl; leer oder fehlend → kein Filter „Thema“. */
  themen?: { id: string; name: string; anzahl: number }[];
  /** Gewähltes Thema; null = alle. */
  themaFilter?: string | null;
  onThemaFilterChange?: (themaId: string | null) => void;
}

export const InspectionFilterBar: React.FC<Props> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  filterMode,
  itemCount,
  onAddItem,
  onMobileAdd,
  onAiAssistGlobal,
  language = 'de',
  onKatalog,
  themen,
  themaFilter = null,
  onThemaFilterChange
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 md:gap-4">
          <div>
              <h2 className="text-lg font-semibold text-slate-800">
                  {filterMode === 'active' ? (language === 'en' ? 'Open Issues' : 'Aktuelle Feststellungen') : (language === 'en' ? 'Closed / History' : 'Erledigte / Historie')}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">{itemCount} {language === 'en' ? 'entries' : 'Einträge'}</p>
          </div>
          
          <div className="flex gap-2 w-full md:w-auto empty:hidden">
              {filterMode === 'active' && (
                  <>
                      {/* Nur am Desktop: mobil steckt der Textvorschlag im
                          Erfassungs-Wizard selbst, dort gibt es genau einen Knopf. */}
                      <div className="hidden md:block">
                          <Button variant="ai" onClick={onAiAssistGlobal}>
                              <Sparkles className="w-4 h-4 mr-2" /> {language === 'en' ? 'Text suggestion' : 'Textvorschlag'}
                          </Button>
                      </div>
                      {/* Standardmangel aus dem Katalog (Torte) — wie der Textvorschlag
                          nur am Desktop; mobil steckt die Torte im Wizard. */}
                      {onKatalog && (
                          <div className="hidden md:block">
                              <Button variant="outline" onClick={onKatalog} title={language === 'en' ? 'Add an entry from the defect catalogue' : 'Neuen Eintrag aus dem Mängelkatalog anlegen'}>
                                  <PieChart className="w-4 h-4 mr-2" /> {language === 'en' ? 'Standard defect' : 'Standardmangel'}
                              </Button>
                          </div>
                      )}
                      {/* Mobil startet "Neu" ausschließlich den Wizard (onMobileAdd) —
                          der Desktop-onAddItem erzeugte mobil eine leere Desktop-Karte.
                          Flaches Blau statt Verlauf: die ! sind nötig, weil Variante
                          und className in derselben Utility-Schicht liegen und dort
                          die CSS-Reihenfolge entscheidet, nicht die im className. */}
                      {onMobileAdd && (
                          <div className="md:hidden w-full">
                              <Button variant="primary" onClick={onMobileAdd} className="w-full h-11 font-semibold! bg-none! bg-blue-600! hover:bg-blue-700! active:bg-blue-700! shadow-none! ring-0!">
                                  <Plus className="w-4 h-4" /> {language === 'en' ? 'Add Entry' : 'Neuer Eintrag'}
                              </Button>
                          </div>
                      )}
                      <div className="hidden md:block">
                          <Button id="btn-add-item" data-tutorial="mangel.neuer-eintrag" variant="primary" onClick={onAddItem}>
                              <Plus className="w-4 h-4 mr-2" /> {language === 'en' ? 'Add Entry' : 'Neuer Eintrag'}
                          </Button>
                      </div>
                  </>
              )}
          </div>
      </div>

      {/* Handy: Suche in eigener Zeile, Status und Thema teilen sich die
          zweite. In EINER Zeile war sie breiter als der Bildschirm — das
          Thema wurde abgeschnitten („Thema: all") und die ganze Liste
          liess sich seitlich schieben. Ab md wie bisher nebeneinander. */}
      <div className="flex flex-wrap gap-2 md:flex-nowrap md:gap-4">
          <div className="relative basis-full md:basis-auto md:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                  type="text" 
                  placeholder={language === 'en' ? 'Search...' : 'Suchen...'}
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full h-11 md:h-10 pl-10 pr-4 rounded-lg bg-white text-base text-slate-800 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
              />
          </div>
          {filterMode === 'active' && (
              <div className="flex flex-1 md:flex-initial min-w-0 items-center gap-1 h-11 md:h-10 bg-white rounded-lg">
                  <div className="pl-3 shrink-0"><Filter className="w-4 h-4 text-slate-400" /></div>
                  <select 
                      aria-label="Status"
                      value={statusFilter} 
                      onChange={(e) => onStatusFilterChange(e.target.value)}
                      className="bg-transparent text-sm outline-none focus:text-blue-600 cursor-pointer h-full px-2 text-slate-700 font-medium min-w-0 w-full md:w-auto"
                  >
                      <option value="all">{language === 'en' ? 'All statuses' : 'Alle Status'}</option>
                      <option value="open">{language === 'en' ? 'Open' : 'Offen'}</option>
                      <option value="info">{language === 'en' ? 'Info' : 'Hinweis'}</option>
                      <option value="good">{language === 'en' ? 'Positive' : 'Positiv'}</option>
                      <option value="general">Information</option>
                  </select>
              </div>
          )}
          {/* Filter „Thema“ (Katalog-Kategorie): nur, wenn mindestens ein
              Eintrag der Liste eine Kategorie trägt. */}
          {themen && themen.length > 0 && (
              <div className="flex flex-1 md:flex-initial min-w-0 items-center gap-1 h-11 md:h-10 bg-white rounded-lg">
                  <div className="pl-3 shrink-0"><Layers className="w-4 h-4 text-slate-400" /></div>
                  <select
                      aria-label={language === 'en' ? 'Topic' : 'Thema'}
                      value={themaFilter ?? ''}
                      onChange={(e) => onThemaFilterChange?.(e.target.value || null)}
                      className="bg-transparent text-sm outline-none focus:text-blue-600 cursor-pointer h-full px-2 text-slate-700 font-medium min-w-0 w-full md:w-auto md:max-w-[14rem]"
                  >
                      <option value="">{language === 'en' ? 'Topic: all' : 'Thema: alle'}</option>
                      {themen.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} ({t.anzahl})</option>
                      ))}
                  </select>
              </div>
          )}
      </div>
    </div>
  );
};